import { Users, Calendar, Video, ClipboardList } from "lucide-react";
import { ProviderSidebar } from "@/components/layout/ProviderSidebar";
import { ProviderHeader } from "@/components/layout/ProviderHeader";
import { StatCard } from "@/components/dashboard/StatCard";
import { TelehealthSchedule } from "@/components/dashboard/TelehealthSchedule";
import { RemoteMonitoring } from "@/components/dashboard/RemoteMonitoring";
import { AuditLogViewer } from "@/components/dashboard/AuditLogViewer";
import { AssignedPatients } from "@/components/dashboard/AssignedPatients";
import { SdohDashboardWidget } from "@/components/dashboard/SdohDashboardWidget";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

export default function ProviderDashboard() {
  const { user } = useAuth();

  const { data: stats } = useQuery({
    queryKey: ["provider-dashboard-stats", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const today = format(new Date(), "yyyy-MM-dd");
      const startOfWeek = new Date();
      startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
      const weekStart = format(startOfWeek, "yyyy-MM-dd");

      const [assignmentsRes, todaySessionsRes, weekSessionsRes, casesRes] = await Promise.all([
        // Active patients (assigned members)
        supabase
          .from("provider_members")
          .select("member_id", { count: "exact", head: true })
          .eq("provider_id", user.id),

        // Today's appointments
        supabase
          .from("telehealth_sessions")
          .select("id, scheduled_time, status")
          .eq("provider_id", user.id)
          .eq("scheduled_date", today)
          .in("status", ["scheduled", "waiting", "in_progress"]),

        // Telehealth sessions this week
        supabase
          .from("telehealth_sessions")
          .select("id", { count: "exact", head: true })
          .eq("provider_id", user.id)
          .gte("scheduled_date", weekStart)
          .lte("scheduled_date", today),

        // Open cases: assigned members who have any session
        supabase
          .from("provider_members")
          .select("member_id")
          .eq("provider_id", user.id),
      ]);

      // For open cases, count members with sessions
      let openCases = 0;
      let needsReview = 0;
      if (casesRes.data && casesRes.data.length > 0) {
        const memberIds = casesRes.data.map((a: any) => a.member_id);
        const { data: sessions } = await supabase
          .from("telehealth_sessions")
          .select("member_id, status, scheduled_date")
          .in("member_id", memberIds);

        if (sessions) {
          const memberSessionMap = new Map<string, any[]>();
          sessions.forEach(s => {
            const list = memberSessionMap.get(s.member_id) || [];
            list.push(s);
            memberSessionMap.set(s.member_id, list);
          });
          openCases = memberSessionMap.size;
          // Count members with no upcoming appointments as "needs review"
          memberSessionMap.forEach((memberSessions) => {
            const hasUpcoming = memberSessions.some(
              (s: any) => s.status === "scheduled" && new Date(s.scheduled_date) >= new Date()
            );
            const hasActive = memberSessions.some(
              (s: any) => s.status === "in_progress" || s.status === "waiting"
            );
            if (!hasUpcoming && !hasActive) needsReview++;
          });
        }
      }

      // Find next appointment time
      const todaySessions = todaySessionsRes.data || [];
      const now = new Date();
      const upcomingSorted = todaySessions
        .filter(s => {
          const [h, m] = (s.scheduled_time || "00:00").split(":").map(Number);
          const sessionTime = new Date();
          sessionTime.setHours(h, m, 0, 0);
          return sessionTime > now && s.status === "scheduled";
        })
        .sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));

      let nextApptLabel = "No more today";
      if (upcomingSorted.length > 0) {
        const [h, m] = (upcomingSorted[0].scheduled_time || "00:00").split(":").map(Number);
        const d = new Date();
        d.setHours(h, m, 0, 0);
        nextApptLabel = `Next: ${format(d, "h:mm a")}`;
      }

      return {
        activePatients: assignmentsRes.count || 0,
        todayAppointments: todaySessions.length,
        weekSessions: weekSessionsRes.count || 0,
        openCases,
        needsReview,
        nextApptLabel,
      };
    },
    enabled: !!user?.id,
  });

  return (
    <div className="min-h-screen bg-background">
      <ProviderSidebar />
      <div className="ml-64">
        <ProviderHeader />
        <main className="p-6">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="font-display text-3xl font-bold text-foreground">Provider Dashboard</h1>
            <p className="mt-1 text-muted-foreground">
              Welcome back. Here's your practice overview.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Active Patients"
              value={stats ? String(stats.activePatients) : "—"}
              change="Assigned to you"
              changeType="neutral"
              icon={Users}
              iconColor="primary"
              linkTo="/provider/patients"
            />
            <StatCard
              title="Open Cases"
              value={stats ? String(stats.openCases) : "—"}
              change={stats ? `${stats.needsReview} require attention` : ""}
              changeType={stats && stats.needsReview > 0 ? "negative" : "neutral"}
              icon={ClipboardList}
              iconColor="warning"
              linkTo="/provider/cases"
            />
            <StatCard
              title="Today's Appointments"
              value={stats ? String(stats.todayAppointments) : "—"}
              change={stats?.nextApptLabel || ""}
              changeType="neutral"
              icon={Calendar}
              iconColor="accent"
              linkTo="/provider/schedule"
            />
            <StatCard
              title="Telehealth Sessions"
              value={stats ? String(stats.weekSessions) : "—"}
              change="This week"
              changeType="neutral"
              icon={Video}
              iconColor="success"
              linkTo="/provider/telehealth"
            />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Left Column */}
            <div className="space-y-6">
              <AssignedPatients />
              <TelehealthSchedule />
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <SdohDashboardWidget />
              <RemoteMonitoring />
            </div>
          </div>

          {/* Full Width Sections */}
          <div className="mt-6 space-y-6">
            <RemoteMonitoring />
            <AuditLogViewer />
          </div>
        </main>
      </div>
    </div>
  );
}
